import React, { useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'

import { ChatContextSourceType } from '@trustero/trustero-api-web/lib/chatcontext/chatcontext_pb'
import { MODEL_TYPE } from '@trustero/trustero-api-web/lib/common/model_pb'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import { ControlPolicyInfo } from 'src/components/Reusable/ListItem/ListItem.components'
import { CONTROL_STATUS } from '@trustero/trustero-api-web/lib/model/control_pb'
import { generatePermalinkFromId } from 'src/components/PageLayout/Permalink/generatePermalink'
import {
  ChatContext,
  ChatContextSelection,
  useChatContextControls,
} from 'src/components/Reusable/ChatContext/useChatContext.hooks'
import {
  ContextIdSelector,
  GridRowLinkItem,
} from '../chatContextPanel.components'
import {
  ContextSelectorTableText,
  ContextSelectorTitleContainer,
} from '../chatContextPanel.styles'

export type ControlModalProps = ControlModalBasicProps & {
  context: ChatContext
  setContext: (context: ChatContext) => void
}

export const ControlModal = ({
  show,
  hide,
  onBack,
  context,
  setContext,
}: ControlModalProps): JSX.Element => {
  const currentSelection = useMemo(() => {
    return context[ChatContextSourceType.CONTROL]
  }, [context])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      setContext({
        ...context,
        [ChatContextSourceType.CONTROL]: newSelection,
      })
    },
    [context, setContext],
  )

  return (
    <ControlIdSelectorModal
      show={show}
      hide={hide}
      onBack={onBack}
      title="Select Controls"
      description="The AI agent will only consider the controls you select here during your conversation."
      onCommit={onCommit}
      currentSelection={currentSelection}
    />
  )
}

export type ControlModalBasicProps = {
  show: boolean
  hide: () => void
  onBack?: () => void
}

export type ControlIdSelectorModalProps = ControlModalBasicProps & {
  title: string
  description: string
  onCommit: (newSelection: ChatContextSelection, controlIds: string[]) => void
  currentSelection: ChatContextSelection
}

export const ControlIdSelectorModal = ({
  show,
  hide,
  onBack,
  title,
  description,
  onCommit: onCommitOriginal,
  currentSelection,
}: ControlIdSelectorModalProps): JSX.Element => {
  const { data, isLoading, error } = useChatContextControls()
  const params = useParams()

  const items = useMemo(() => {
    return (
      data?.getItemsList()?.map((control) => {
        const controlAsObject = control.toObject()
        return {
          id: control.getId(),
          renderItems: [
            <ContextSelectorTableText key="0-model-id">
              {control.getModelId()}
            </ContextSelectorTableText>,
            <ContextSelectorTitleContainer key="1-title">
              <ControlPolicyInfo
                control={controlAsObject}
                isControlNa={
                  control.getStatus() === CONTROL_STATUS.NOTAPPLICABLE
                }
              />
              <ContextSelectorTableText key="2-name">
                <GridRowLinkItem
                  link={generatePermalinkFromId({
                    pageContext: params.pageContext as string,
                    modelType: MODEL_TYPE.CONTROL,
                    modelId: control.getModelId(),
                    isInternalLink: false,
                  })}
                  text={control.getName()}
                />
              </ContextSelectorTableText>
            </ContextSelectorTitleContainer>,
            <DepartmentChip
              departmentId={control.getDepartmentId()}
              key="3-department-id"
            />,
          ],
        }
      }) ?? []
    )
  }, [data, params.pageContext])

  const onCommit = useCallback(
    (newSelection: ChatContextSelection) => {
      onCommitOriginal(
        newSelection,
        data?.getItemsList().map((control) => control.getId()) ?? [],
      )
    },
    [onCommitOriginal, data],
  )

  return (
    <ContextIdSelector
      title={title}
      description={description}
      itemType="control"
      currentSelection={currentSelection}
      onCommit={onCommit}
      show={show}
      hide={hide}
      onBack={onBack}
      isLoading={isLoading}
      items={items}
      numberOfColumns={3}
      error={error}
    />
  )
}
